import * as React from 'react';
import { makeStyles, createStyles, Box } from '@material-ui/core';
import moment, { isMoment, Moment } from 'moment';
import Btn from '../../components/Btn/Btn';
import { AppliedByType, NegotiationOption, NegotiationOptionAlternative, OutDebt, OutDebtState } from '../../generated/graphql';
import { TranquiTheme } from '../App/theme';
import DebtDetailsNegotiationSummaryForm from './DebtDetailsNegotiationSummaryForm';
import OutDebtSelector from './OutDebtSelector';
import DebtDetailsNegotiationSummaryContent from './DebtDetailsNegotiationSummaryContent';
import { NegotiationProps, useCreateNegotiationHelper } from '../../utils/useCreateNegotiationHelper';
import RenderText from '../../dictionaries/RenderText';
import { getNegBooleans } from '../../utils/getNegBooleans';
import AlertBase from '../../components/Alerts/AlertBase';
import { getNormative026ContentKey } from '../../utils/getNormative026ContentKey';
import { useDebtNegExtraContext } from './DebtNegExtraContext';
import useDictionary from '../../dictionaries/context/useDictionary';
import MarkdownRender from '../../dictionaries/MarkdownRender';
import { formatIRate } from '../../utils/formatIRate';
import { getCommissionPercentText } from '../../utils/getCommissionPercentText';
import { getWeightedRateText } from '../../utils/getWeightedRateText';
import { useNonPaymentReasonFormContext } from './NonPaymentReasonFormContext';
import { isCypressTest } from '../../isCypressTest';
import { useSpace } from '../../content/utils/useSpace';

interface DebtDetailsNegotiationSummaryProps {
  onBackToOptions: () => void;
  onConfirm: () => void;
  negOption: NegotiationOption;
  alternative?: NegotiationOptionAlternative;
  outDebt: OutDebt;
  debts: OutDebt[];
}

const DebtDetailsNegotiationSummary: React.FC<DebtDetailsNegotiationSummaryProps> = (props) => {
  const { onBackToOptions, onConfirm, outDebt, alternative, negOption, debts } = props;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const [ createNegotiation ] = useCreateNegotiationHelper();
  const { info } = useDebtNegExtraContext();
  const nonPaymentReasonFormContext = useNonPaymentReasonFormContext();
  const { hasFormErrors } = nonPaymentReasonFormContext || {};

  const {
    isNoOptions,
    isACPK,
    isDav,
    isPunished,
    isApex,
    isTotalPayment,
    isVirtualDebt,
    isBogotaTermExtension,
  } = getNegBooleans(outDebt, negOption, alternative);

  const handleBack = () => onBackToOptions();

  const handleConfirmNegotiation = async (extraInfo: any = {}) => {
    if (!alternative) return;
    try {
      let paymentDate;
      if (isMoment(extraInfo.paymentDate)) paymentDate = (extraInfo.paymentDate as Moment).startOf('day').toDate();
      if (info.paymentPromiseDate) paymentDate = moment(info.paymentPromiseDate).startOf('day').toDate();
      const extraInfoToSend: NegotiationProps = {
        outDebtId: outDebt.id,
        alternative,
        bank: outDebt.bank,
        extraInfo: {
          ...extraInfo,
          newPaymentDay: parseInt(extraInfo?.newPaymentDay, 10) || undefined,
          paymentDate,
          appliedBy: AppliedByType.Web,
          maxPaymentDate: paymentDate || moment(negOption.settings?.maxPaymentDate).toDate(),
        },
        negotiationOption: negOption,
        negotiationType: negOption.type,
      };
      if (isACPK && !extraInfo.newPaymentDay && paymentDate) {
        extraInfoToSend.extraInfo.newPaymentDay = moment(paymentDate).date();
      }
      await createNegotiation(extraInfoToSend);
      onConfirm();
      localStorage.removeItem('formValues');
    } catch (e) {
      handleBack();
    }
  };

  const scrollRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (isCypressTest) return;
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 30);
  }, []);

  const backBtn = (
    <Btn
      variant="outlined"
      color="secondary"
      onClick={handleBack}
      data-testid="debt-details-negotiation-summary__summary-view-go-back-button"
    >
      <RenderText textKey="action_goBack_button_summaryView" />
    </Btn>
  );

  const forwardBtn = (
    <Btn
      className="ml-2"
      variant="contained"
      type="submit"
      data-testid="debt-details-negotiation-summary__summary-view-sent-button"
      disabled={hasFormErrors}
    >
      <RenderText textKey={isBogotaTermExtension ? 'negotiation_TermExtension_btn_label' : 'action_sent_button_summaryView'} />
    </Btn>
  );

  const { gT } = useDictionary();
  const c026Text = gT(getNormative026ContentKey(negOption, outDebt, undefined, alternative) || '', {
    bank: outDebt.bank,
    monthlyRate: formatIRate((negOption?.rate || outDebt?.rate), 'm'),
    annualRate: formatIRate((negOption?.rate || outDebt?.rate), 'y'),
    paymentDay: moment(outDebt.paymentDate).date(),
    commissionPercent: `${((alternative?.commissionPercent || 0) * 100).toFixed(2)}%`,
    commissionPercentBulletText: getCommissionPercentText(gT, 'commissionPercentBulletText', alternative?.commissionPercent || 0),
    weightedRateText: getWeightedRateText(gT, outDebt.extra?.popupRaw || ''),
    negType: negOption.type,
  });

  const davAlertText = gT(`negotiation_${negOption.type}${isPunished ? `_${OutDebtState.Punished}` : ''}_consequences`, {
    bank: outDebt.bank,
    monthlyRate: formatIRate((negOption?.rate || outDebt?.rate), 'm'),
    annualRate: formatIRate((negOption?.rate || outDebt?.rate), 'y'),
    paymentDay: moment(outDebt.paymentDate).date(),
    commissionPercent: `${((alternative?.commissionPercent || 0) * 100).toFixed(2)}%`,
    commissionPercentBulletText: getCommissionPercentText(gT, 'commissionPercentBulletText', alternative?.commissionPercent || 0),
    negType: negOption.type,
  });

  const ApexAlertText = useSpace('NEG_OPTION_SUMMARY', 'alert', { outDebt, option: negOption, alternative }) || ' ';

  const isDocumentalTermExtension = isBogotaTermExtension && !!negOption.settings?.isDocumentalTermExtension;

  return (
    <>
      <div ref={scrollRef} className={classes.scrollRef} />
      {
        isNoOptions
        && <OutDebtSelector className={classes.debtSelector} />
      }

      <DebtDetailsNegotiationSummaryContent
        outDebt={outDebt}
        negOption={negOption}
        alternative={alternative}
      />
      <Box mt={2} />
      <DebtDetailsNegotiationSummaryForm
        onConfirm={handleConfirmNegotiation}
        outDebt={outDebt}
        negOption={negOption}
        alternative={alternative}
        outDebts={debts}
      >
        {c026Text && (
          <AlertBase title="Ten en cuenta que" type="informative" filled={false}>
            <span className="t-prewrap"><MarkdownRender text={c026Text} /></span>
          </AlertBase>
        )}
        {(!c026Text && isDav && !isNoOptions) && (
        <AlertBase title="Ten en cuenta que" type="informative" filled={false}>
          <span className="t-prewrap"><MarkdownRender text={davAlertText} /></span>
        </AlertBase>
        )}
        {(isApex && ApexAlertText.text && (isTotalPayment || isVirtualDebt || isACPK)) && (
        <AlertBase title="Ten en cuenta que" type="informative" filled={false}>
          <span className="t-prewrap"><MarkdownRender text={ApexAlertText.text} /></span>
        </AlertBase>
        )}
        {isDocumentalTermExtension && (
        <AlertBase type="informative" className="my-2">
          <MarkdownRender text={gT('termExtension_documental_alert')} />
        </AlertBase>
        )}
        <Box className={classes.btnWrapper}>
          {isNoOptions && debts.length < 2 ? <Box /> : backBtn}
          {!isNoOptions && forwardBtn}
        </Box>
      </DebtDetailsNegotiationSummaryForm>
    </>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  btnWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    marginTop: theme.spacing(3),
    '& > *:nth-child(1)': {
      flexShrink: 1,
    },
    '& > *:nth-child(2)': {
      flexShrink: 2,
    },
  },
  scrollRef: {
    transform: 'translate(0px, -100px)',
  },
  debtSelector: {
    margin: theme.spacing(0, -2),
    marginBottom: theme.spacing(2),
    position: 'sticky',
    top: theme.spacing(0),
    zIndex: 5,
    background: theme.palette.pearl.main,
    padding: theme.spacing(1, 2, 0),
  },
}));

export default DebtDetailsNegotiationSummary;
